import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hiddenFields", "inspectorFields", "previewFrame", "titleInput", "typeSelect", "visibleInput",
    "heroCtaTextInput", "heroModalBody", "heroModalOverlay", "heroModalTemplateDescription",
    "heroModalTemplateTitle", "heroNameInput", "heroPreviewFrame", "heroPreviewFrameWrap",
    "heroTaglineInput", "heroTitleInput"]

  static values = {
    heroTemplates: String,
    previewUrl: String,
    schema: String,
    section: String,
    msgPreviewRefreshError: { type: String, default: "미리보기를 새로고침할 수 없습니다." },
    msgFieldAddLabel: { type: String, default: "항목 추가" },
    msgHeroModalTitle: { type: String, default: "히어로 템플릿 미리보기" },
    msgHeroModalDescription: { type: String, default: "선택한 히어로 템플릿을 이 영역에서 확인할 수 있습니다." }
  }

  connect() {
    this.submitting = false
    this.previewTimer = null
    this.previewRequestId = 0
    this.activeInlineFieldPath = null
    this.activeInspectorFieldPath = null
    this.heroTemplates = this.parseJson(this.heroTemplatesValue, [])
    this.heroModalPreviewTemplateId = null
    this.heroModalDevice = "pc"
    this.heroModalOpen = false
    this.schema = this.parseJson(this.schemaValue, {})
    this.section = this.normalizeSection(this.parseJson(this.sectionValue, {}))
    this.labels = this.schema.labels || {}
    this.sectionTypes = this.uniqueTypes()
    this.initialSnapshot = this.snapshot()

    this.setupStaticBindings()
    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged("initial")
  }

  disconnect() {
    this.removeStaticBindings()
    window.removeEventListener("beforeunload", this.beforeUnload)
    window.clearTimeout(this.previewTimer)
  }

  refreshPreview(event) {
    event?.preventDefault()
    this.queuePreviewRefresh({ immediate: true })
  }

  updateTitle = (event) => {
    this.section.title = event.currentTarget.value
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  updateType = (event) => {
    const nextType = event.currentTarget.value
    const previousType = this.section.section_type
    const previousTitle = (this.section.title || "").trim()

    this.section.section_type = nextType
    this.section.content_data = this.deepClone(this.starterContentFor(nextType))

    if (!previousTitle || previousTitle === this.labelFor(previousType)) {
      this.section.title = this.labelFor(nextType)
    }

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  updateVisible = (event) => {
    this.section.visible = event.currentTarget.checked
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  updateField(event) {
    const input = event.target
    const path = input.dataset.path
    if (!path) return

    const value = input.type === "checkbox" ? input.checked : input.value
    this.setNestedValue(this.section.content_data, path, value)
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  addRepeaterItem(event) {
    event.preventDefault()

    const name = event.currentTarget.dataset.repeaterName
    const limit = Number(event.currentTarget.dataset.limit || 0)
    const schema = this.fieldSchemaFor(this.section.section_type).find((field) => field.name === name && field.type === "repeater")
    if (!schema) return

    const items = Array.isArray(this.section.content_data[name]) ? this.section.content_data[name] : []
    if (limit > 0 && items.length >= limit) return

    items.push(this.blankRepeaterItem(schema.fields || []))
    this.section.content_data[name] = items

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  removeRepeaterItem(event) {
    event.preventDefault()

    const name = event.currentTarget.dataset.repeaterName
    const index = Number(event.currentTarget.dataset.index)
    const items = Array.isArray(this.section.content_data[name]) ? this.section.content_data[name] : []
    if (Number.isNaN(index) || index < 0 || index >= items.length) return

    items.splice(index, 1)
    this.section.content_data[name] = items

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  startInlineEditingFromPreview = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || !this.previewFrameTarget.contains(editable)) return

    event.preventDefault()
    event.stopPropagation()
    this.startInlineEditing(editable)
  }

  handleInlineBlur = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || editable.dataset.inlineEditing !== "true") return

    this.commitInlineEditing(editable)
  }

  handleInlineKeydown = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || editable.dataset.inlineEditing !== "true") return

    const multiline = editable.dataset.inlineMultiline === "true"

    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelInlineEditing(editable)
      return
    }

    if (!multiline && event.key === "Enter") {
      event.preventDefault()
      editable.blur()
    }
  }

  handleInlinePaste = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || editable.dataset.inlineEditing !== "true") return

    event.preventDefault()
    const text = event.clipboardData?.getData("text/plain") || ""
    document.execCommand("insertText", false, text)
  }

  handleSubmit = () => {
    this.submitting = true
    this.syncHiddenFields()
    const btn = document.querySelector('[onclick*="profile-page-section-designer-form"]')
    if (btn) {
      btn.disabled = true
      btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> 저장 중...`
      btn.style.opacity = "0.7"
      btn.style.pointerEvents = "none"
    }
  }

  beforeUnload = (event) => {
    if (this.submitting || !this.isDirty()) return

    event.preventDefault()
    event.returnValue = ""
  }

  async performPreviewRefresh() {
    if (!this.hasPreviewFrameTarget || !this.previewUrlValue) return

    const requestId = ++this.previewRequestId
    const formData = this.buildPreviewPayload()

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        body: formData
      })

      const html = await response.text()
      if (!response.ok) throw new Error(html || this.msgPreviewRefreshErrorValue)
      if (requestId !== this.previewRequestId) return

      this.previewFrameTarget.innerHTML = html
      this.clearPreviewError()
      this.applyLinkedFocusState()
    } catch (error) {
      console.error(error)
      this.showPreviewError()
    }
  }

  setupStaticBindings() {
    this.element.addEventListener("submit", this.handleSubmit)
    this.titleInputTarget.addEventListener("input", this.updateTitle)
    this.typeSelectTarget.addEventListener("change", this.updateType)
    this.visibleInputTarget.addEventListener("change", this.updateVisible)
    window.addEventListener("profile-page-designer:apply-section-content", this.handleApplySectionContent)
    window.addEventListener("profile-page-designer:update-images", this.handleUpdateImages)
    window.addEventListener("profile-page-designer:request-selection", this.handleSelectionRequest)
    window.addEventListener("profile-page-designer:activate-section", this.handleActivateSection)
    this.previewFrameTarget.addEventListener("dblclick", this.startInlineEditingFromPreview)
    this.previewFrameTarget.addEventListener("focusout", this.handleInlineBlur)
    this.previewFrameTarget.addEventListener("keydown", this.handleInlineKeydown)
    this.previewFrameTarget.addEventListener("paste", this.handleInlinePaste)
    this.inspectorFieldsTarget.addEventListener("focusin", this.handleInspectorFocusIn)
    this.inspectorFieldsTarget.addEventListener("focusout", this.handleInspectorFocusOut)
    window.addEventListener("beforeunload", this.beforeUnload)
  }

  removeStaticBindings() {
    this.element.removeEventListener("submit", this.handleSubmit)
    this.titleInputTarget.removeEventListener("input", this.updateTitle)
    this.typeSelectTarget.removeEventListener("change", this.updateType)
    this.visibleInputTarget.removeEventListener("change", this.updateVisible)
    window.removeEventListener("profile-page-designer:apply-section-content", this.handleApplySectionContent)
    window.removeEventListener("profile-page-designer:update-images", this.handleUpdateImages)
    window.removeEventListener("profile-page-designer:request-selection", this.handleSelectionRequest)
    window.removeEventListener("profile-page-designer:activate-section", this.handleActivateSection)
    this.previewFrameTarget.removeEventListener("dblclick", this.startInlineEditingFromPreview)
    this.previewFrameTarget.removeEventListener("focusout", this.handleInlineBlur)
    this.previewFrameTarget.removeEventListener("keydown", this.handleInlineKeydown)
    this.previewFrameTarget.removeEventListener("paste", this.handleInlinePaste)
    this.inspectorFieldsTarget.removeEventListener("focusin", this.handleInspectorFocusIn)
    this.inspectorFieldsTarget.removeEventListener("focusout", this.handleInspectorFocusOut)
  }

  renderInspector() {
    this.titleInputTarget.value = this.section.title || ""
    this.visibleInputTarget.checked = !!this.section.visible
    this.typeSelectTarget.innerHTML = this.sectionTypes.map((type) => {
      const selected = type === this.section.section_type ? " selected" : ""
      return `<option value="${this.escapeHtml(type)}"${selected}>${this.escapeHtml(this.labelFor(type))}</option>`
    }).join("")

    this.renderInspectorFields()
    this.applyLinkedFocusState()
  }

  renderInspectorFields() {
    const schema = this.fieldSchemaFor(this.section.section_type)
    if (schema.length === 0) {
      this.inspectorFieldsTarget.innerHTML = `
        <div class="ppd-field">
          <p class="ppd-help">이 섹션 타입은 구조 편집만 지원합니다.</p>
        </div>
      `
      return
    }

    this.inspectorFieldsTarget.innerHTML = schema.map((field) => this.renderField(field, this.section.content_data[field.name], field.name, this.section.client_key)).join("")
  }

  renderField(field, value, path, clientKey) {
    const label = `<label>${this.escapeHtml(field.label)}</label>`
    const safePath = this.escapeHtml(path)
    const wrapperStart = `<div class="ppd-field ppd-field-shell" data-client-key="${this.escapeHtml(clientKey)}" data-field-path="${safePath}">`
    const wrapperEnd = `</div>`

    switch (field.type) {
      case "textarea":
        return `
          ${wrapperStart}
            ${label}
            <textarea class="ppd-textarea" rows="${field.rows || 3}" data-path="${safePath}" data-action="input->profile-page-section-designer#updateField change->profile-page-section-designer#updateField" placeholder="${this.escapeHtml(field.placeholder || "")}">${this.escapeHtml(value || "")}</textarea>
          ${wrapperEnd}
        `
      case "select":
        return `
          ${wrapperStart}
            ${label}
            <select class="ppd-select" data-path="${safePath}" data-action="change->profile-page-section-designer#updateField">
              ${(field.options || []).map(([text, optionValue]) => {
                const selected = String(optionValue) === String(value ?? "") ? " selected" : ""
                return `<option value="${this.escapeHtml(optionValue)}"${selected}>${this.escapeHtml(text)}</option>`
              }).join("")}
            </select>
          ${wrapperEnd}
        `
      case "checkbox":
        return `
          ${wrapperStart}
            <label class="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" ${value ? "checked" : ""} data-path="${safePath}" data-action="change->profile-page-section-designer#updateField">
              <span class="text-sm text-gray-700">${this.escapeHtml(field.label)}</span>
            </label>
          ${wrapperEnd}
        `
      case "repeater":
        return this.renderRepeaterField(field, value, path, clientKey)
      default:
        return `
          ${wrapperStart}
            ${label}
            <input type="text" class="ppd-input" value="${this.escapeHtml(value || "")}" data-path="${safePath}" data-action="input->profile-page-section-designer#updateField change->profile-page-section-designer#updateField" placeholder="${this.escapeHtml(field.placeholder || "")}">
          ${wrapperEnd}
        `
    }
  }

  renderRepeaterField(field, value, path, clientKey) {
    const items = Array.isArray(value) ? value : []
    const list = items.map((item, index) => `
      <div class="ppd-repeater__item">
        <div class="ppd-repeater__controls">
          <strong class="text-sm text-gray-900">${this.escapeHtml(field.label)} ${index + 1}</strong>
          <button type="button"
                  class="btn-secondary text-xs"
                  data-repeater-name="${this.escapeHtml(field.name)}"
                  data-index="${index}"
                  data-action="profile-page-section-designer#removeRepeaterItem">
            삭제
          </button>
        </div>
        ${(field.fields || []).map((nestedField) => this.renderField(nestedField, item?.[nestedField.name], `${path}.${index}.${nestedField.name}`, clientKey)).join("")}
      </div>
    `).join("")

    const disableAdd = field.limit && items.length >= field.limit

    return `
      <div class="ppd-field">
        <div class="flex items-center justify-between gap-3 mb-2">
          <label class="mb-0">${this.escapeHtml(field.label)}</label>
          <button type="button"
                  class="btn-secondary text-xs ${disableAdd ? "opacity-50 pointer-events-none" : ""}"
                  data-repeater-name="${this.escapeHtml(field.name)}"
                  data-limit="${field.limit || ""}"
                  data-action="profile-page-section-designer#addRepeaterItem">
            ${this.escapeHtml(field.add_label || this.msgFieldAddLabelValue)}
          </button>
        </div>
        <div class="ppd-repeater">${list || '<p class="ppd-help">아직 항목이 없습니다.</p>'}</div>
      </div>
    `
  }

  startInlineEditing(editable) {
    const fieldPath = editable.dataset.fieldPath
    if (!fieldPath) return

    if (this.activeInlineFieldPath && this.activeInlineFieldPath !== fieldPath) {
      const current = this.previewFrameTarget.querySelector("[data-inline-editing='true']")
      if (current) this.commitInlineEditing(current)
    }

    this.activeInlineFieldPath = fieldPath
    this.activeInspectorFieldPath = fieldPath
    editable.dataset.inlineEditing = "true"
    editable.dataset.originalValue = this.getNestedValue(this.section.content_data || {}, fieldPath) || ""
    editable.classList.add("ppd-inline-text--editing")
    editable.setAttribute("contenteditable", "true")
    editable.focus()
    this.renderInspector()
    this.scrollInspectorFieldIntoView(fieldPath)
  }

  commitInlineEditing(editable) {
    const fieldPath = editable.dataset.fieldPath
    if (!fieldPath) return

    const multiline = editable.dataset.inlineMultiline === "true"
    const nextValue = this.normalizeInlineText(editable.innerText, multiline)
    this.setNestedValue(this.section.content_data, fieldPath, nextValue)

    editable.removeAttribute("contenteditable")
    editable.dataset.inlineEditing = "false"
    editable.classList.remove("ppd-inline-text--editing")
    delete editable.dataset.originalValue

    this.activeInlineFieldPath = null
    this.syncHiddenFields()
    this.renderInspector()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  cancelInlineEditing(editable) {
    const originalValue = editable.dataset.originalValue || ""
    editable.textContent = originalValue
    editable.removeAttribute("contenteditable")
    editable.dataset.inlineEditing = "false"
    editable.classList.remove("ppd-inline-text--editing")
    delete editable.dataset.originalValue
    this.activeInlineFieldPath = null
    this.applyLinkedFocusState()
  }

  handleInspectorFocusIn = (event) => {
    const wrapper = event.target.closest("[data-field-path][data-client-key]")
    if (!wrapper || !this.inspectorFieldsTarget.contains(wrapper)) return

    this.activeInspectorFieldPath = wrapper.dataset.fieldPath
    this.applyLinkedFocusState()
    this.scrollPreviewFieldIntoView(this.activeInspectorFieldPath)
  }

  handleInspectorFocusOut = () => {
    window.setTimeout(() => {
      if (this.inspectorFieldsTarget.contains(document.activeElement)) return
      if (this.activeInlineFieldPath) return

      this.activeInspectorFieldPath = null
      this.applyLinkedFocusState()
    }, 0)
  }

  queuePreviewRefresh({ immediate = false } = {}) {
    window.clearTimeout(this.previewTimer)
    if (immediate) {
      this.performPreviewRefresh()
      return
    }

    this.previewTimer = window.setTimeout(() => this.performPreviewRefresh(), 120)
  }

  showPreviewError() {
    this.clearPreviewError()
    const frame = this.previewFrameTarget?.closest(".ppd-frame")
    if (!frame) return
    const el = document.createElement("div")
    el.className = "ppd-preview-error"
    el.style.cssText = "display:flex;align-items:center;gap:8px;padding:10px 14px;background:#FEF2F2;border:1px solid #FECACA;color:#991B1B;font-size:13px;margin-bottom:12px;"
    el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><span>미리보기를 불러오지 못했습니다. 다시 시도해 주세요.</span>`
    frame.insertBefore(el, frame.firstChild)
  }

  clearPreviewError() {
    this.previewFrameTarget?.closest(".ppd-frame")?.querySelectorAll(".ppd-preview-error").forEach(el => el.remove())
  }

  buildPreviewPayload() {
    const formData = new FormData()
    formData.append("authenticity_token", this.csrfToken())
    this.appendFormValue(formData, "sections[0]", this.serializableSection())
    return formData
  }

  syncHiddenFields() {
    const inputs = []
    this.appendHiddenInputs(inputs, "sections[0]", this.serializableSection())
    this.hiddenFieldsTarget.innerHTML = inputs.join("")
  }

  serializableSection() {
    return {
      id: this.section.id || "",
      client_key: this.section.client_key,
      title: this.section.title || "",
      section_type: this.section.section_type,
      visible: this.section.visible ? "1" : "0",
      position: String(this.section.position || 0),
      _destroy: "0",
      style_variant: this.section.style_variant || "",
      content_data: this.deepClone(this.section.content_data || {}),
      content_data_json: JSON.stringify(this.section.content_data || {})
    }
  }

  openHeroModal(event) {
    event?.preventDefault()
    this.heroModalOpen = true
    this.ensureHeroModalPreviewTemplate()
    this.renderHeroModal()
    if (this.hasHeroModalOverlayTarget) {
      this.heroModalOverlayTarget.classList.add("is-open")
      document.body.style.overflow = "hidden"
    }
  }

  closeHeroModal(event) {
    event?.preventDefault()
    this.heroModalOpen = false
    if (this.hasHeroModalOverlayTarget) {
      this.heroModalOverlayTarget.classList.remove("is-open")
      document.body.style.overflow = ""
    }
  }

  applyHeroTemplateAndClose(event) {
    event?.preventDefault()
    const templateName = this.heroModalPreviewTemplate()?.name || null
    this.closeHeroModal()
    this.emitSelectionChanged("user")
    window.dispatchEvent(new CustomEvent("profile-page-designer:hero-template-applied", {
      detail: { templateName }
    }))
  }

  closeHeroModalOnBackdrop(event) {
    if (event.target === this.heroModalOverlayTarget) {
      this.closeHeroModal()
    }
  }

  selectHeroTemplate(event) {
    event.preventDefault()
    const templateId = event.currentTarget.dataset.heroTemplateId
    const template = this.heroTemplateById(templateId)
    if (!template) return

    this.heroModalPreviewTemplateId = String(template.id)
    this.section.style_variant = template.style_variant
    this.section.content_data = this.deepClone(template.content_data || {})

    this.renderHeroModal()
    this.renderInspector()
    this.syncHiddenFields()
    this.queuePreviewRefresh({ immediate: true })
  }

  switchHeroPreviewDevice(event) {
    event.preventDefault()
    this.heroModalDevice = event.currentTarget.dataset.device || "pc"
    this.renderHeroModal()
  }

  updateHeroField(event) {
    const input = event.target
    const path = input.dataset.heroPath
    if (!path) return

    const value = input.type === "checkbox" ? input.checked : input.value
    this.setNestedValue(this.section, path, value)

    this.renderHeroModal()
    this.syncHiddenFields()
    this.queuePreviewRefresh()
  }

  renderHeroModal() {
    const contentData = this.section.content_data || {}
    if (this.hasHeroNameInputTarget) this.heroNameInputTarget.value = contentData.name || ""
    if (this.hasHeroTitleInputTarget) this.heroTitleInputTarget.value = contentData.title || ""
    if (this.hasHeroTaglineInputTarget) this.heroTaglineInputTarget.value = contentData.tagline || ""
    if (this.hasHeroCtaTextInputTarget) this.heroCtaTextInputTarget.value = contentData.cta_text || ""

    const previewTemplate = this.heroModalPreviewTemplate()
    if (this.hasHeroModalTemplateTitleTarget) {
      this.heroModalTemplateTitleTarget.textContent = previewTemplate?.name || this.msgHeroModalTitleValue
    }
    if (this.hasHeroModalTemplateDescriptionTarget) {
      this.heroModalTemplateDescriptionTarget.textContent = previewTemplate?.description || this.msgHeroModalDescriptionValue
    }
    if (this.hasHeroPreviewFrameTarget) {
      const nextSrc = previewTemplate?.preview_url || ""
      if (this.heroPreviewFrameTarget.src !== nextSrc) {
        this.heroPreviewFrameTarget.src = nextSrc
      } else {
        this.syncHeroPreviewFrameHeight()
      }
    }
    if (this.hasHeroPreviewFrameWrapTarget) {
      this.heroPreviewFrameWrapTarget.dataset.device = this.heroModalDevice
    }
    if (this.hasHeroModalBodyTarget) {
      this.heroModalBodyTarget.dataset.device = this.heroModalDevice
    }

    this.element.querySelectorAll("[data-hero-template-id]").forEach((card) => {
      card.classList.toggle("is-selected", card.dataset.heroTemplateId === String(this.heroModalPreviewTemplateId || ""))
    })

    this.element.querySelectorAll(".ppd-hero-modal__device").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.device === this.heroModalDevice)
    })

    this.syncHeroPreviewFrameHeight()
  }

  ensureHeroModalPreviewTemplate() {
    if (this.heroModalPreviewTemplateId && this.heroTemplateById(this.heroModalPreviewTemplateId)) return

    const currentVariant = this.section.style_variant
    if (currentVariant) {
      const match = this.heroTemplates.find((t) => t.style_variant === currentVariant)
      if (match) {
        this.heroModalPreviewTemplateId = String(match.id)
        return
      }
    }

    this.heroModalPreviewTemplateId = String(this.heroTemplates[0]?.id || "")
  }

  heroTemplateById(id) {
    if (!id) return null
    return this.heroTemplates.find((t) => String(t.id) === String(id)) || null
  }

  heroModalPreviewTemplate() {
    return this.heroTemplateById(this.heroModalPreviewTemplateId)
  }

  syncHeroPreviewFrameHeight() {
    if (!this.hasHeroPreviewFrameTarget) return
    window.requestAnimationFrame(() => {
      const frame = this.heroPreviewFrameTarget
      const doc = frame.contentDocument || frame.contentWindow?.document
      if (!doc) return
      const body = doc.body
      const html = doc.documentElement
      const documentHeight = Math.max(
        body?.scrollHeight || 0, body?.offsetHeight || 0,
        html?.scrollHeight || 0, html?.offsetHeight || 0
      )
      frame.style.height = `${Math.max(documentHeight, 400)}px`
    })
  }

  appendHiddenInputs(inputs, prefix, value) {
    if (Array.isArray(value)) {
      value.forEach((nestedValue, index) => this.appendHiddenInputs(inputs, `${prefix}[${index}]`, nestedValue))
      return
    }

    if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, nestedValue]) => this.appendHiddenInputs(inputs, `${prefix}[${key}]`, nestedValue))
      return
    }

    inputs.push(`<input type="hidden" name="${this.escapeHtml(prefix)}" value="${this.escapeHtml(value ?? "")}">`)
  }

  appendFormValue(formData, prefix, value) {
    if (Array.isArray(value)) {
      value.forEach((nestedValue, index) => this.appendFormValue(formData, `${prefix}[${index}]`, nestedValue))
      return
    }

    if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, nestedValue]) => this.appendFormValue(formData, `${prefix}[${key}]`, nestedValue))
      return
    }

    formData.append(prefix, value ?? "")
  }

  normalizeSection(section) {
    return {
      id: section.id || null,
      client_key: section.client_key || `persisted-${section.id}`,
      title: section.title || this.labelFor(section.section_type),
      section_type: section.section_type,
      visible: this.booleanValue(section.visible),
      position: section.position || 0,
      persisted: !!section.persisted || !!section.id,
      content_data: this.deepClone(section.content_data || {}),
      style_variant: section.style_variant || null
    }
  }

  uniqueTypes() {
    const collected = [
      ...(this.schema.all_types || []),
      ...(this.schema.fallback_types || []),
      ...(this.schema.editable_types || []),
      this.section.section_type
    ]

    return [...new Set(collected.filter(Boolean))]
  }

  fieldSchemaFor(sectionType) {
    return this.schema.field_schemas?.[sectionType] || []
  }

  starterContentFor(sectionType) {
    return this.deepClone(this.schema.starter_content?.[sectionType] || {})
  }

  blankRepeaterItem(fields) {
    return (fields || []).reduce((memo, field) => {
      memo[field.name] = field.type === "checkbox" ? false : ""
      return memo
    }, {})
  }

  setNestedValue(target, path, value) {
    const keys = path.split(".")
    let cursor = target

    keys.forEach((key, index) => {
      const isLast = index === keys.length - 1
      const nextKey = keys[index + 1]
      const nextIsIndex = /^\d+$/.test(nextKey)

      if (isLast) {
        cursor[key] = value
        return
      }

      if (cursor[key] == null) {
        cursor[key] = nextIsIndex ? [] : {}
      }

      cursor = cursor[key]
    })
  }

  getNestedValue(target, path) {
    return path.split(".").reduce((current, key) => {
      if (current == null) return null
      return current[key]
    }, target)
  }

  labelFor(sectionType) {
    return this.labels?.[sectionType] || sectionType
  }

  booleanValue(value) {
    return value === true || value === "true" || value === "1" || value === 1
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ||
      this.element.querySelector("input[name='authenticity_token']")?.value ||
      ""
  }

  snapshot() {
    return JSON.stringify({
      id: this.section.id || null,
      title: this.section.title || "",
      section_type: this.section.section_type,
      visible: !!this.section.visible,
      content_data: this.section.content_data || {}
    })
  }

  isDirty() {
    return this.snapshot() !== this.initialSnapshot
  }

  parseJson(value, fallback) {
    try {
      return JSON.parse(value || "null") ?? fallback
    } catch (_error) {
      return fallback
    }
  }

  deepClone(value) {
    return JSON.parse(JSON.stringify(value))
  }

  normalizeInlineText(value, multiline) {
    const normalized = String(value || "").replace(/\u00a0/g, " ")
    if (multiline) {
      return normalized.replace(/\n{3,}/g, "\n\n").trim()
    }

    return normalized.replace(/\s+/g, " ").trim()
  }

  linkedFocusFieldPath() {
    return this.activeInlineFieldPath || this.activeInspectorFieldPath
  }

  applyLinkedFocusState() {
    this.previewFrameTarget.querySelectorAll(".is-linked-focus").forEach((node) => node.classList.remove("is-linked-focus"))
    this.previewFrameTarget.querySelectorAll(".is-linked-section").forEach((node) => node.classList.remove("is-linked-section"))
    this.inspectorFieldsTarget.querySelectorAll(".is-linked-focus").forEach((node) => node.classList.remove("is-linked-focus"))

    const fieldPath = this.linkedFocusFieldPath()
    if (!fieldPath) return

    this.findPreviewFieldNode(fieldPath)?.classList.add("is-linked-focus")
    this.findPreviewSectionNode()?.classList.add("is-linked-section")
    this.findInspectorFieldNode(fieldPath)?.classList.add("is-linked-focus")
  }

  findPreviewFieldNode(fieldPath) {
    return this.previewFrameTarget.querySelector(
      `[data-inline-editable='true'][data-client-key="${this.escapeSelector(this.section.client_key)}"][data-field-path="${this.escapeSelector(fieldPath)}"]`
    )
  }

  findPreviewSectionNode() {
    return this.previewFrameTarget.querySelector(`.ppd-canvas-section[data-client-key="${this.escapeSelector(this.section.client_key)}"]`)
  }

  findInspectorFieldNode(fieldPath) {
    return this.inspectorFieldsTarget.querySelector(
      `[data-client-key="${this.escapeSelector(this.section.client_key)}"][data-field-path="${this.escapeSelector(fieldPath)}"]`
    )
  }

  scrollPreviewFieldIntoView(fieldPath) {
    this.findPreviewFieldNode(fieldPath)?.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" })
  }

  scrollInspectorFieldIntoView(fieldPath) {
    this.findInspectorFieldNode(fieldPath)?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
  }

  handleSelectionRequest = () => {
    this.emitSelectionChanged("request")
  }

  handleActivateSection = () => {
    this.emitSelectionChanged("user")
  }

  handleApplySectionContent = (event) => {
    const payload = event.detail?.data
    if (!payload) return

    console.debug("[ProfilePageSectionDesigner] apply-section-content received", payload)

    if (payload.title !== undefined) {
      this.section.title = String(payload.title || "").trim()
    }

    if (payload.style_variant && this.section.section_type === "hero") {
      console.debug("[ProfilePageSectionDesigner] applying style_variant", payload.style_variant)
      this.section.style_variant = payload.style_variant
    }

    // __generate__ 값을 빈 문자열로 치환 — 프리뷰 404 방지
    const safeContentData = this.stripGeneratePlaceholders(payload.content_data)

    console.debug("[ProfilePageSectionDesigner] field schema before merge", (this.fieldSchemaFor(this.section.section_type) || []).map((field) => field.name))
    this.section.content_data = this.mergeSectionContentData(
      this.section.content_data || {},
      safeContentData,
      this.fieldSchemaFor(this.section.section_type)
    )
    console.debug("[ProfilePageSectionDesigner] section content after merge", this.section.content_data)

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  stripGeneratePlaceholders(contentData) {
    if (!contentData || typeof contentData !== "object") return contentData
    const cd = { ...contentData }
    for (const [key, val] of Object.entries(cd)) {
      if (val === "__generate__") {
        cd[key] = ""
      } else if (Array.isArray(val)) {
        cd[key] = val.map(item => {
          if (!item || typeof item !== "object") return item
          const clone = { ...item }
          for (const [k, v] of Object.entries(clone)) {
            if (v === "__generate__") clone[k] = ""
          }
          return clone
        })
      }
    }
    return cd
  }

  handleUpdateImages = (event) => {
    const updates = event.detail?.updates
    if (!Array.isArray(updates) || updates.length === 0) return

    for (const update of updates) {
      const { field, value, arrayKey, index } = update
      if (!this.section?.content_data) continue

      if (arrayKey != null && index != null) {
        if (Array.isArray(this.section.content_data[arrayKey]) && this.section.content_data[arrayKey][index]) {
          this.section.content_data[arrayKey][index][field] = value
        }
      } else {
        this.section.content_data[field] = value
      }
    }

    this.syncHiddenFields()
    this.queuePreviewRefresh({ immediate: true })
  }

  emitSelectionChanged(interactionSource = "sync") {
    window.dispatchEvent(new CustomEvent("profile-page-designer:selection-changed", {
      detail: {
        selectedSurface: "section",
        clientKey: this.section.client_key,
        id: this.section.id || null,
        sectionType: this.section.section_type,
        title: this.section.title || null,
        contentData: this.deepClone(this.section.content_data || {}),
        fieldSchema: this.deepClone(this.fieldSchemaFor(this.section.section_type)),
        label: this.labelFor(this.section.section_type),
        visible: !!this.section.visible,
        styleVariant: this.section.style_variant || null,
        interactionSource
      }
    }))
  }

  mergeSectionContentData(currentContent, incomingContent, fieldSchema) {
    const nextContent = this.deepClone(currentContent || {})
    if (!incomingContent || typeof incomingContent !== "object" || Array.isArray(incomingContent)) return nextContent

    const schemaByName = new Map((fieldSchema || []).map((field) => [field.name, field]))
    console.debug("[ProfilePageSectionDesigner] mergeSectionContentData incoming keys", Object.keys(incomingContent || {}))
    Object.entries(incomingContent).forEach(([key, value]) => {
      const field = schemaByName.get(key)
      if (!field) return

      nextContent[key] = this.deepClone(value)
    })

    return nextContent
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  escapeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(value)
    return String(value).replace(/"/g, '\\"')
  }
}
